// ============================================================================
// apps/web/src/lib/use-my-tasks-count.ts
// ----------------------------------------------------------------------------
// Badge « nouvelles tâches » de l'entrée To-Do (sidebar, tiroir mobile, tuile
// accueil), dans la logique du badge « nouveau CV ». NOMINATIF : on rattache
// l'utilisateur Magic connecté à un collaborateur de la to-do MM Distribution
// (projet Supabase SÉPARÉ) — via l'override éditable `todo_user_link.mm_collab_id`,
// sinon par rapprochement de nom.
//
// Comme les tâches « ne bougent pas vite », le badge ne compte PAS tout le
// backlog mais seulement les tâches NON VUES : celles qui te sont assignées et
// créées APRÈS ta dernière ouverture de la To-Do (`todo_user_link.seen_at`,
// synchronisé entre appareils). Au clic sur To-Do → marqué vu → retombe à 0.
//
// Rafraîchi au montage, au focus/visibilité, toutes les 60 s, et via l'event
// interne quand une autre vue marque « vu ». Best-effort (backends séparés).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';
import { resolveMMCollabId, fetchMMNewTaskCount } from './mm-task-client';

// Table/fonction absentes des types générés (db:types requiert un token).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase.from.bind(supabase) as unknown as (t: string) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = supabase.rpc.bind(supabase) as unknown as (fn: string) => Promise<any>;

const EVENT = 'magic:todo-seen';

export function useMyOpenTasksCount(): { count: number; markSeen: () => void } {
  const { me } = useAuth();
  const userId = me?.profile.id ?? null;
  const first = me?.profile.first_name ?? null;
  const last = me?.profile.last_name ?? null;

  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!userId) { setCount(0); return; }
    try {
      // 1) Liaison : override explicite du collaborateur + marqueur « vu ».
      const { data: link } = await db('todo_user_link')
        .select('mm_collab_id, seen_at')
        .eq('user_id', userId)
        .maybeSingle();
      const collabId: number | null =
        (link?.mm_collab_id ?? null) != null
          ? Number(link.mm_collab_id)
          : await resolveMMCollabId(first, last);
      if (collabId == null) { setCount(0); return; }

      // 2) Jamais ouvert → on cale le marqueur à maintenant (le backlog existant
      //    n'est pas signalé comme « nouveau ») et on part de 0.
      const seen: string | null = link?.seen_at ?? null;
      if (!seen) { await rpc('todo_mark_seen'); setCount(0); return; }

      // 3) Nouvelles = tâches ouvertes qui me sont assignées, créées après « vu ».
      setCount(await fetchMMNewTaskCount(collabId, seen));
    } catch { /* best-effort : projet MM / table séparés */ }
  }, [userId, first, last]);

  // Marque la To-Do comme vue (clic sur l'entrée) → badge à 0 partout.
  const markSeen = useCallback(() => {
    setCount(0);
    void rpc('todo_mark_seen');
    window.dispatchEvent(new Event(EVENT));
  }, []);

  useEffect(() => {
    void refresh();
    const onFocus = () => { void refresh(); };
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onSeen = () => { setCount(0); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(EVENT, onSeen);
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(EVENT, onSeen);
      window.clearInterval(id);
    };
  }, [refresh]);

  return { count, markSeen };
}
